import { useMemo, useState, type ReactNode } from "react";
import { useUploadQueue, UploadQueueItem } from "../../contexts/UploadQueueContext";
import AbortUploadsModal from "./AbortUploadsModal";
import OverlayRoot from "./OverlayRoot";

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let idx = 0;
  let value = bytes;
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024;
    idx += 1;
  }
  const digits = idx === 0 ? 0 : idx === 1 ? 0 : 1;
  return `${value.toFixed(digits)} ${units[idx]}`;
}

function statusLabel(item: UploadQueueItem): string {
  switch (item.status) {
    case "queued":
      return "Queued";
    case "uploading":
      return "Uploading";
    case "success":
      return "Uploaded";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
    case "duplicate":
      return "Already in album";
    default:
      return item.status;
  }
}

function statusColor(item: UploadQueueItem): string {
  switch (item.status) {
    case "success":
      return "text-green-300";
    case "failed":
      return "text-red-300";
    case "cancelled":
      return "text-gray-300";
    case "duplicate":
      return "text-amber-200";
    case "uploading":
      return "text-blue-200";
    case "queued":
    default:
      return "text-gray-200";
  }
}

function partitionItems(items: UploadQueueItem[]) {
  const failed: UploadQueueItem[] = [];
  const queued: UploadQueueItem[] = [];
  const duplicates: UploadQueueItem[] = [];
  const finished: UploadQueueItem[] = [];

  for (const item of items) {
    if (item.status === "failed") {
      failed.push(item);
    } else if (item.status === "queued" || item.status === "uploading") {
      queued.push(item);
    } else if (item.status === "duplicate") {
      duplicates.push(item);
    } else if (item.status === "success" || item.status === "cancelled") {
      finished.push(item);
    }
  }

  queued.sort((a, b) => {
    if (a.status === "uploading" && b.status !== "uploading") return -1;
    if (b.status === "uploading" && a.status !== "uploading") return 1;
    return 0;
  });
  finished.sort((a, b) => (a.finishedAt ?? 0) - (b.finishedAt ?? 0));
  failed.sort((a, b) => (a.finishedAt ?? 0) - (b.finishedAt ?? 0));
  duplicates.sort((a, b) => (a.finishedAt ?? 0) - (b.finishedAt ?? 0));

  return { failed, queued, duplicates, finished };
}

function UploadItemRow({
  item,
  onCancel,
  onRetry,
}: {
  item: UploadQueueItem;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
}) {
  const isActive = item.status === "queued" || item.status === "uploading";
  const canRetry = item.status === "failed" || item.status === "cancelled";
  const showProgress = item.status !== "duplicate";

  return (
    <div data-section="upload-toast-item" className="px-4 py-3 border-b border-white/5 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-white text-sm font-medium truncate">{item.fileName}</div>
          <div className="mt-0.5 flex items-center gap-2 text-xs">
            <span className={statusColor(item)}>{statusLabel(item)}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-300">{formatBytes(item.fileSize)}</span>
            {showProgress && (
              <>
                <span className="text-gray-400">•</span>
                <span className="text-gray-200">{item.percent}%</span>
              </>
            )}
          </div>
          {item.error && item.status === "failed" && (
            <div className="mt-1 text-xs text-red-200/90 line-clamp-2">{item.error}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isActive && (
            <button
              onClick={() => onCancel(item.id)}
              className="text-xs text-gray-200 hover:text-white px-2 py-1 rounded-lg hover:bg-white/10 transition-all"
            >
              Cancel
            </button>
          )}
          {canRetry && (
            <button
              onClick={() => onRetry(item.id)}
              className="text-xs text-gray-200 hover:text-white px-2 py-1 rounded-lg hover:bg-white/10 transition-all"
            >
              Retry
            </button>
          )}
        </div>
      </div>

      {showProgress && (
        <div className="mt-2 h-2 bg-white/10 rounded-full overflow-hidden">
          <div
            className={[
              "h-full transition-all duration-200",
              item.status === "failed"
                ? "bg-red-500/80"
                : item.status === "success"
                  ? "bg-green-500/80"
                  : "jp-gradient-progress",
            ].join(" ")}
            style={{ width: `${item.percent}%` }}
          />
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, count, action }: { title: string; count: number; action?: ReactNode }) {
  return (
    <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-2 bg-black/50 backdrop-blur-md border-b border-white/10">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-300">
        {title} <span className="text-gray-400">({count})</span>
      </div>
      {action}
    </div>
  );
}

export default function UploadToast() {
  const {
    items,
    activeCount,
    cancel,
    retry,
    retryAllFailed,
    abortAll,
    resumeCancelled,
    dismiss,
    show,
    isVisible,
    persistWarning,
  } = useUploadQueue();
  const [isAbortConfirmOpen, setIsAbortConfirmOpen] = useState(false);

  const { failed, queued, duplicates, finished } = useMemo(() => partitionItems(items), [items]);

  const hasAny = items.length > 0;
  const hasActive = activeCount > 0;
  const cancelledCount = finished.filter((item) => item.status === "cancelled").length;

  if (!hasAny && !hasActive) return null;

  if (!isVisible && hasActive) {
    return (
      <OverlayRoot data-section="upload-toast-root" isolate="contain" portal className="fixed bottom-4 right-4 z-[60]">
        <button
          onClick={show}
          className="px-4 py-2 rounded-full bg-black/60 backdrop-blur-xl border border-white/20 text-white shadow-xl hover:bg-black/70 transition-all"
        >
          Uploading ({activeCount})
        </button>
      </OverlayRoot>
    );
  }

  if (!isVisible) return null;

  return (
    <>
      <OverlayRoot
        data-section="upload-toast-root"
        isolate="contain"
        portal
        className="fixed bottom-4 right-4 z-[60] w-[min(420px,calc(100vw-2rem))]"
      >
        <div className="bg-black/60 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[rgb(var(--primary-green-rgb))] shadow-[0_0_12px_rgba(var(--primary-green-rgb),0.8)]" />
              <div className="text-white font-semibold">Uploads</div>
              {hasActive && (
                <span className="text-xs text-gray-200 bg-white/10 border border-white/10 rounded-full px-2 py-0.5">
                  {activeCount} active
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasActive ? (
                <button
                  onClick={() => setIsAbortConfirmOpen(true)}
                  className="text-xs text-red-200 hover:text-white px-2 py-1 rounded-lg hover:bg-white/10 transition-all"
                  title="Abort remaining uploads"
                >
                  Abort
                </button>
              ) : cancelledCount > 0 ? (
                <button
                  onClick={resumeCancelled}
                  className="text-xs text-green-200 hover:text-white px-2 py-1 rounded-lg hover:bg-white/10 transition-all"
                  title="Resume aborted uploads"
                >
                  Resume
                </button>
              ) : null}
              <button
                onClick={dismiss}
                className="text-gray-200 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all"
                aria-label="Close uploads"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {hasActive && (
            <div className="px-4 py-2 text-xs text-gray-300 border-b border-white/10">
              Uploads pause if you leave this page. Reopen joint.photos to continue — you won&apos;t need to pick the
              files again.
            </div>
          )}
          {persistWarning && (
            <div className="px-4 py-2 text-xs text-amber-200 border-b border-white/10">{persistWarning}</div>
          )}

          <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
            {failed.length > 0 && (
              <section>
                <SectionHeader
                  title="Failed"
                  count={failed.length}
                  action={
                    <button
                      onClick={retryAllFailed}
                      className="text-xs text-red-200 hover:text-white px-2 py-1 rounded-lg hover:bg-white/10 transition-all"
                    >
                      Retry all
                    </button>
                  }
                />
                {failed.map((item) => (
                  <UploadItemRow key={item.id} item={item} onCancel={cancel} onRetry={retry} />
                ))}
              </section>
            )}

            {queued.length > 0 && (
              <section>
                <SectionHeader title="Queued" count={queued.length} />
                {queued.map((item) => (
                  <UploadItemRow key={item.id} item={item} onCancel={cancel} onRetry={retry} />
                ))}
              </section>
            )}

            {duplicates.length > 0 && (
              <section>
                <SectionHeader title="Duplicates" count={duplicates.length} />
                {duplicates.map((item) => (
                  <UploadItemRow key={item.id} item={item} onCancel={cancel} onRetry={retry} />
                ))}
              </section>
            )}

            {finished.length > 0 && (
              <section>
                <SectionHeader title="Finished" count={finished.length} />
                {finished.map((item) => (
                  <UploadItemRow key={item.id} item={item} onCancel={cancel} onRetry={retry} />
                ))}
              </section>
            )}
          </div>
        </div>
      </OverlayRoot>
      <AbortUploadsModal
        isOpen={isAbortConfirmOpen}
        activeCount={activeCount}
        onClose={() => setIsAbortConfirmOpen(false)}
        onConfirm={() => {
          abortAll();
          setIsAbortConfirmOpen(false);
        }}
      />
    </>
  );
}
